<template>
  <template v-if="question.status !== 'READ_ONLY'">
    <div
        v-click-outside="handleFocusOut"
        class="inputContainer"
        :class="{
      'container-menu-opened': isMenuOpened,
      invalid: error && hasBeenInteractedWith,
      adjust: inputTempValue,
    }"
        role="combobox"
        :style="{ pointerEvents: disabled ? 'none' : 'auto', minHeight: '51px' }"
        aria-haspopup="listbox"
        @click="focusInput(true); $emit('user-interaction')"
        v-bind="$attrs"
    >
      <div class="d-flex flex-wrap chip-container">
        <div
            :id="`values-output-${i}`"
            v-for="(selection, i) in selectedValues"
            :key="`${selection.value}_${i}`"
        >
          <div
              class="d-inline-block chip ps-2 pe-2 me-2 fw-bold"
              @click.stop.prevent="handleRemove(i)"
          >
            <span data-testid="selected-value">{{ selection.text }}</span>
            <i>
              <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="ms-2"
                  height="12"
                  width="12"
                  viewBox="0 0 384 512"
              >
                <!--! Font Awesome Free 6.4.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2023 Fonticons, Inc. -->
                <path
                    d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"
                />
              </svg>
            </i>
          </div>
        </div>
        <input
            aria-label="Select an option"
            :aria-expanded="isMenuOpened"
            :aria-controls="itemsListId"
            class="quill-question-input choice pt-0"
            ref="autoCompleteInput"
            type="text"
            :name="`${question.name}`"
            v-model="inputTempValue"
            :readonly="disabled"
            @focusin="inputFocused = true"
            @focusout="inputFocused = false"
            @keydown="handleKeyDown"
            @input="handleInput"
        />
      </div>

      <span
          class="quill-question-placeholder placeholder-wrap"
          :class="{
        'placeholder-float':
          selectedValues?.length || inputFocused || isMenuOpened,
      }"
      >
      {{
          forcePromptPlaceholder
              ? question.prompt
              : question.placeholder || question.prompt
        }}
    </span>

      <ul
          :id="itemsListId"
          role="listbox"
          ref="suggestionList"
          aria-multiselectable="true"
          class="user-select-none"
      >
        <li v-if="loadingItems">loading items...</li>
        <li
            class="noOptions"
            role="option"
            v-else-if="!filteredItems.length"
            disabled
            @click.prevent="() => {}"
        >
          No options found
        </li>
        <template v-for="(option, index) in filteredItems" :key="index">
          <li
              :id="option?.value ?? option?.text ?? index"
              role="option"
              :aria-selected="currentFocusedItem === index"
              @mousedown.prevent="setChoiceValue(option)"
              @mousemove="currentFocusedItem = index"
              :class="{
            selected: selectedMap[option[bindingProp]],
            item_focused: currentFocusedItem === index,
          }"
          >
            <svg width="12" height="1em" v-if="selectedMap[option[bindingProp]]">
              <use href="/sprite.svg#checkbox-checked" />
            </svg>
            <svg width="12" height="1em" v-else>
              <use href="/sprite.svg#checkbox-unchecked" />
            </svg>
            {{ getItemText(option) }}
          </li>
        </template>
      </ul>
      <i role="button" id="chevron" @click.stop.prevent="handleArrorClick(!isMenuOpened)">
        <svg @click.stop.prevent="handleArrorClick(!isMenuOpened)" width="12" height="10" color="red"><use href="/sprite.svg#chevron-down"/></svg>
      </i>
    </div>
    <slot name="helptext" :isConfirm="!disableConfirm && question.value && !error" :isError="error"
          :confirmCb="() => emitUpdate()"></slot>
  </template>
  <ReadOnly
      v-else
      :question="question"
      :value="this.selectedValues.length ? this.selectedValues?.map((option) => (option || {})[this.bindingProp])?.join(','):'-'"
      :forcePromptPlaceholder="forcePromptPlaceholder"
  />
</template>

<script>
//TODO: IF LIST SUFFERS FROM RENDER SLOWDOWN DUE TO SIZE, IMPLEMENT VIRTUALIZATION TO SPEED IT UP https://vueuse.org/core/useVirtualList/ , https://github.com/Akryum/vue-virtual-scroller/blob/master/packages/vue-virtual-scroller/README.md --> TWO GOOD OPTIONS
import ReadOnly from "@/components/waffle/components/form-components/ReadOnly.vue";

export default {
  name: "ChoicesInput",
  components: { ReadOnly },
  props: {
    disabled: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    question: {
      type: Object,
      required: true,
    },
    itemText: {
      type: String,
      required: false,
    },
    returnObject: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    itemValue: {
      type: String,
      required: false,
    },
    loadingItems: {
      type: Boolean,
      required: false,
    },
    modelValue: {
      type: [String, Object],
      required: false,
    },
    disableFiltering: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    hideSubText: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    forcePromptPlaceholder: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    skipEagerValidation: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    disableConfirm: Boolean,
    hasBeenInteractedWith: Boolean,
  },
  data() {
    return {
      inputTempValue: "",
      selectedValues: [],
      isTyping: false,
      error: "",
      isMenuOpened: false,
      inputFocused: false,
      itemsListId: `id_${Math.random().toString(36).substr(2, 13)}`, // random id,
      currentFocusedItem: null,
      shouldTriggerEvent: false,
    };
  },
  mounted() {
    this.question.value?.split(",").forEach((value) => {
      const found = (this.question.options || [])?.find((item) => {
        return item[this.bindingProp]?.includes(value);
      });
      if (found) {
        this.selectedValues.push(found);
      }
    });

    if (this.question.error) {
      this.$nextTick(() => {
        this.error = this.question.error;
        return this.$emit("field-error", true);
      });
    }

    if (
        this.question.required &&
        !this.selectedValues?.length &&
        !this.skipEagerValidation
    ) {
      this.error = "Required";
      return this.$emit("field-error", true);
    }
  },
  computed: {
    bindingDisplay() {
      return this.itemText || "text";
    },
    bindingProp() {
      return this.itemValue || "value";
    },
    filteredItems() {
      const itemIsObject =
          this.question.options && typeof this.question.options[0] === "object";
      const pattern = new RegExp(
          this.inputTempValue ? this.inputTempValue.toLowerCase() : ""
      );
      return (this.question.options || []).filter((item) => {
        if (!this.inputTempValue || !this.isTyping) {
          return true;
        }
        return pattern.test(
            itemIsObject
                ? item[this.bindingDisplay].toLowerCase()
                : item.toLowerCase()
        );
      });
    },
    selectedMap() {
      return this.selectedValues?.reduce((acc, curr) => {
        acc[curr[this.bindingProp]] = true;
        return acc;
      }, {});
    },
  },
  emits: ["update:modelValue", "field-error", "confirm",'user-interaction'],
  watch: {
    selectedValues: {
      handler(newVal) {
        if (!newVal?.length && this.question.required) {
          this.error = "Required";
          this.$emit("field-error", true);
        } else {
          this.error = "";
          this.$emit("field-error", false);
        }
      },
      deep: true,
    },
    question: {
      handler(newQuestion) {
        if (newQuestion.error) {
          this.error = newQuestion.error;
          return this.$emit("field-error", true);
        }
        if (newQuestion.value) {
          this.selectedValues = [];
        }
        newQuestion.value?.split(",").forEach((value) => {
          const found = (this.question.options || [])?.find((item) => {
            return item[this.bindingProp]?.includes(value);
          });
          if (found) {
            this.selectedValues.push(found);
          }
        });
        this.inputTempValue = this.selectedValue?.[this.bindingDisplay] ?? "";
      },
      deep: true,
    },
  },
  methods: {
    handleArrorClick(menuNextState) {
      this.changeMenuVisibility(menuNextState);
      if (!menuNextState) {
        this.handleFocusOut(true);
      }
    },
    handleRemove(index) {
      this.selectedValues.splice(index, 1);
      this.focusInput(true);
    },
    changeMenuVisibility(value) {
      this.isMenuOpened = value;
    },

    handleArrowKeyNavigation(isUpArrow, currentIndex, itemsLength) {
      let newIndex = (currentIndex + (isUpArrow ? -1 : 1)) % itemsLength;
      if (newIndex < 0) {
        newIndex = itemsLength - 1;
      }
      this.currentFocusedItem = newIndex;
      this.scrollIntoView(newIndex);
    },

    handleItemSelection(itemsToUse, currentIndex) {
      if (currentIndex !== -1) {
        this.setChoiceValue(itemsToUse[currentIndex]);
      }
    },
    handleKeyDown(e) {
      const currentIndex = this.currentFocusedItem;
      const itemsLength = this.filteredItems.length;

      this.changeMenuVisibility(true);

      const isUpArrow = e.key === "ArrowUp";
      const isDownArrow = e.key === "ArrowDown";
      const isEnter = e.key === "Enter";
      const isTab = e.key === "Tab";

      // Handle keyboard navigation
      if (isUpArrow || isDownArrow) {
        e.preventDefault();
        this.handleArrowKeyNavigation(isUpArrow, currentIndex, itemsLength);
      } else if (isEnter && itemsLength > 0 && currentIndex !== -1) {
        e.preventDefault();
        this.handleItemSelection(this.filteredItems, currentIndex);
      } else if (isTab) {
        this.changeMenuVisibility(false);
      }
    },

    // New method to scroll to the focused item
    scrollIntoView(index) {
      const focusedItem = this.$refs.suggestionList.children[index];
      if (focusedItem) {
        focusedItem.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }
    },
    getItemText(option) {
      return this.itemText
          ? option[this.itemText]
          : typeof option === "object"
              ? option.text
              : option;
    },
    handleInput(e) {
      if (
          this.selectedValue &&
          this.selectedValue[this.bindingDisplay] !== e.target.value
      ) {
        this.selectedValue = null;
      }
      this.changeMenuVisibility(true);
      this.isTyping = true;

      this.currentFocusedItem = 0;
    },
    handleFocusOut(skip = false) {
      if (!this.isMenuOpened && !skip) return;

      this.changeMenuVisibility(false);
      if (!this.filteredItems.length) {
        this.inputTempValue = "";
        return;
      }
      setTimeout(() => {
        if (!this.selectedValue) {
          this.inputTempValue = "";
        }
        this.emitUpdate();
      }, 250);
    },
    focusInput(visibility = false) {
      this.$refs.autoCompleteInput.focus();
      this.inputFocused = true;
      this.changeMenuVisibility(visibility);
    },
    emitUpdate() {
      const returnVal = this.returnObject
          ? this.selectedValues
          : this.selectedValues
              ?.map((option) => (option || {})[this.bindingProp])
              ?.join(",");

      this.$emit("update:modelValue", returnVal?.length ? returnVal : null);
      if (this.question.value) {
        this.$emit("confirm");
      }
    },
    setChoiceValue(option) {
      this.isTyping = false;

      const isSelected = this.selectedMap[option[this.bindingProp]];
      if (option && !isSelected) {
        this.selectedValues.push(option);
      } else {
        this.selectedValues = this.selectedValues.filter(
            (x) => x[this.bindingProp] !== option[this.bindingProp]
        );
      }
      this.inputTempValue = null;
      if (this.disableConfirm) {
        this.emitUpdate();
      }
    },
  },

  directives: {
    "click-outside": {
      mounted(el, binding) {
        const clickHandler = (e) => {
          if (!el.contains(e.target) && el !== e.target) {
            if (typeof binding.value === "function") binding.value();
          }
        };

        document.addEventListener("click", clickHandler);

        el._clickOutside = clickHandler;
      },
      unmounted(el) {
        document.removeEventListener("click", el._clickOutside);
        delete el._clickOutside;
      },
    },
  },
};
//TODO:CLEAN UP AND REFINE CSS
</script>

<style scoped lang="scss">
.align-text-end {
  text-align: end;
}

.placeholder-float {
  font-size: 12px !important;
  padding: 2px 8px 5px 12px !important;
}

.chip-container {
  padding: 20px 8px 5px 12px;
}

.chip {
  border-radius: 4px;
  background-color: #eeeeee;
  font-size: 12px;
}

.choice {
  border: none;
}

.inputContainer {
  box-sizing: border-box;
  border: 1px solid #ccc;
  border-radius: 3px;
  width: 100%;
  font-size: 16px;
  background: transparent;
}

.noOptions {
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.subtext {
  font-size: 0.75rem;
  font: normal normal normal 12px/18px Roboto;
  letter-spacing: 0px;
  margin-bottom: 4px;
  color: #757575;
}

.errorMessage {
  color: #bb0000;
}

.quill-question-placeholder {
  position: absolute;
  font-weight: normal;
  padding: 10px 12px;
  padding-right: 32px !important;
  line-height: 2em;
  top: 0;
  left: 0;
  color: #757575;
  font-size: 16px;
  pointer-events: none;
  transform: scale(1);
  transition: 0.26s all ease;
}

.quill-question {
  > div {
    position: relative;
    cursor: pointer;

    > div > .quill-question-input {
      box-sizing: border-box;
      border-radius: 3px;
      font-size: 16px;
      background: transparent;
      outline: none;
      font-weight: 500;
    }

    > ul {
      list-style: none;
      padding: 0;
      position: absolute;
      top: calc(100% - 1px);
      background: white;
      border: 1px solid #21212122;
      box-shadow: 3px 3px 10px #21212122;
      width: 100%;
      z-index: 9;
      visibility: hidden;
      transition-delay: 0.13s;
      max-height: 200px;
      overflow: auto;

      > li {
        padding: 2px 15px;
        cursor: pointer;
        font-weight: 400;

        &.selected {
          font-weight: bold;
          color: #525252;
          // background: #e22323;
          background: #efefef;
        }
        &.item_focused {
          font-weight: bold;
          color: #525252;
          // background: #e22323;
          background: #e6e6e6;
        }
      }
    }

    > i {
      display: block;
      width: 10px;
      height: 10px;
      position: absolute;
      right: 16px;
      color: #525252;
      transition: 0.26s all ease;
    }
  }

  > b {
    color: red;
    display: none;
  }

  &.error {
    > b {
      display: block;
    }

    > div {
      border-color: red;
    }
  }

  > u {
    display: block;
    text-align: right;
    text-decoration: underline;
    cursor: pointer;
  }

  #chevron {
    display: block;
    width: 12px;
    height: 12px;
    position: absolute;
    top: 13px;
    right: 12px;
    color: #525252;
    transition: 0.26s all ease;
  }
}

/*TODO convert to scss*/

.quill-question > label > i > svg:hover:hover ~ span {
  visibility: visible;
  transition-delay: 0.31s;
}

.quill-question
> div.inputContainer
.chip-container
+ .quill-question-placeholder {
  font-size: 16px;
  padding: 8px 8px 5px 12px;
}

.quill-question > div.inputContainer.container-menu-opened > ul {
  /*display: block;*/
  visibility: visible;
  transition-delay: 0.1s;
}

.quill-question > div.inputContainer.container-menu-opened > #chevron {
  transform: rotate(180deg);
  top: 30px;
}

.form-floating > .form-control:focus ~ label.form-label-text,
.form-floating > .form-control:focus ~ label {
  font-size: 12px;
  color: #484848;
  padding-top: 1rem;
}

.form-control-disabled:hover {
  border: none;
}

.form-control-enabled:hover {
  border: 2px solid #212121;
}

// End Floating Label CSS

.invalid {
  border: 1px solid #bb0000 !important;

  &:hover,
  &:focus,
  &:focus-within {
    border: 2px solid #bb0000 !important;
  }
}
</style>
